import React, { useState, useRef, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
// @ts-expect-error モジュール 'linkheader-parser/dist/linkheader-parser-node' に型定義が無いため
import * as linkHeaderParser from 'linkheader-parser/dist/linkheader-parser-node';
import Loader from '../common/loader';
import type { Company } from '../../models/companies/company';
import type { Category } from '../../../common/models/categories/category';
import type { Area } from '../../models/companies/areas/area';
import type { Feature } from '../../models/companies/features/feature';
import type { EcfStage } from '../../models/companies/ecf_stages/ecfStage';
import { CompanyClient } from '../../models/companies/companyClient';
import { ConditionList } from './conditionList';
import { CompanyCard } from './companyCard';
import {
  buildRansackQuery,
  fundingBefores,
} from '../../models/companies/company';
import { fundingAfters } from '../../models/companies/company';
import { exitExperiences } from '../../models/companies/company';
import { fundingSchemes } from '../../models/companies/company';
import { angelTaxes } from '../../models/companies/company';
import { sdgs } from '../../models/companies/company';
import { ecfFundings } from '../../models/companies/company';
import { handleAxiosError } from '../../../common/helpers/axiosHelper';
import { simpleFlashMessage } from '../../../common/utils/flashMessage';

type Props = {
  categories: Array<Category>;
  areas: Array<Area>;
  features: Array<Feature>;
  ecfStages: Array<EcfStage>;
};

export default function IndexView(props: Props) {
  const { categories, areas, features, ecfStages } = props;
  const [companies, setCompanies] = useState<Company[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [fundingBefore, setFundingBefore] = useState<number | null>(null);
  const [isOpenFundingBefores, setIsOpenFundingBefores] =
    useState<boolean>(false);
  const [fundingAfter, setFundingAfter] = useState<number | null>(null);
  const [isOpenFundingAfters, setIsOpenFundingAfters] =
    useState<boolean>(false);
  const [exitExperience, setExitExperience] = useState<number | null>(null);
  const [isOpenExitExperiences, setIsOpenExitExperiences] =
    useState<boolean>(false);
  const [category, setCategory] = useState<number | null>(null);
  const [isOpenCategories, setIsOpenCategories] = useState<boolean>(false);
  const [area, setArea] = useState<number | null>(null);
  const [isOpenAreas, setIsOpenAreas] = useState<boolean>(false);
  const [fundingScheme, setFundingScheme] = useState<number | null>(null);
  const [isOpenFundingSchemes, setIsOpenFundingSchemes] =
    useState<boolean>(false);
  const [angelTax, setAngelTax] = useState<number | null>(null);
  const [isOpenAngelTaxes, setIsOpenAngelTaxes] = useState<boolean>(false);
  const [sdg, setSdg] = useState<number | null>(null);
  const [isOpenSdgs, setIsOpenSdgs] = useState<boolean>(false);
  const [feature, setFeature] = useState<number | null>(null);
  const [isOpenFeatures, setIsOpenFeatures] = useState<boolean>(false);
  const [ecfStage, setEcfStage] = useState<number | null>(null);
  const [isOpenEcfStages, setIsOpenEcfStages] = useState<boolean>(false);
  const [ecfFunding, setEcfFunding] = useState<number | null>(null);
  const [isOpenEcfFundings, setIsOpenEcfFundings] = useState<boolean>(false);

  const companyClient = useRef(new CompanyClient());

  const fetchCompanies = () => {
    const query = buildRansackQuery(
      keyword,
      fundingBefore,
      fundingAfter,
      exitExperience,
      category,
      area,
      fundingScheme,
      angelTax,
      sdg,
      feature,
      ecfStage,
      ecfFunding,
    );
    companyClient.current
      .searchCompanies(page, query)
      .then(response => {
        const hasNext =
          response.headers &&
          // eslint-disable-next-line no-prototype-builtins -- eslint:recommended 有効化に伴う既存実装の暫定許容（別途対応）
          response.headers.hasOwnProperty('link') &&
          // eslint-disable-next-line no-prototype-builtins -- eslint:recommended 有効化に伴う既存実装の暫定許容（別途対応）
          linkHeaderParser.parse(response.headers.link).hasOwnProperty('next');
        setCompanies(prev => [...prev, ...response.data]);
        setHasNextPage(hasNext);
        setIsLoading(false);
      })
      .catch(error => {
        handleAxiosError(error, simpleFlashMessage);
      });
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react-hooks ルール有効化に伴う既存実装の暫定許容（別途対応）
  }, []);

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react-hooks ルール有効化に伴う既存実装の暫定許容（別途対応）
  }, [
    fundingBefore,
    fundingAfter,
    exitExperience,
    category,
    area,
    fundingScheme,
    angelTax,
    sdg,
    feature,
    ecfStage,
    ecfFunding,
    page,
  ]);

  const onChangeKeyword = (kw: string) => {
    if (typingTimeout != null) {
      clearTimeout(typingTimeout);
    }
    setCompanies([]);
    setPage(1);
    setIsLoading(true);
    setHasNextPage(true);
    setKeyword(kw);
    setTypingTimeout(
      setTimeout(() => {
        fetchCompanies();
      }, 1000),
    );
  };

  const onClickCondition = (name: string, id: number | null) => {
    if (!isLoading) {
      setCompanies([]);
      setPage(1);
      setIsLoading(true);
      setHasNextPage(true);
      setKeyword('');
      switch (name) {
        case 'fundingBefore':
          setFundingBefore(id);
          break;
        case 'fundingAfter':
          setFundingAfter(id);
          break;
        case 'exitExperience':
          setExitExperience(id);
          break;
        case 'category':
          setCategory(id);
          break;
        case 'area':
          setArea(id);
          break;
        case 'fundingScheme':
          setFundingScheme(id);
          break;
        case 'angelTax':
          setAngelTax(id);
          break;
        case 'sdg':
          setSdg(id);
          break;
        case 'feature':
          setFeature(id);
          break;
        case 'ecfStage':
          setEcfStage(id);
          break;
        case 'ecfFunding':
          setEcfFunding(id);
          break;
        default:
          break;
      }
    }
  };

  const onLoadMore = () => {
    if (!isLoading && hasNextPage) {
      setPage(prev => prev + 1);
      setIsLoading(true);
    }
  };

  return (
    <>
      <div className="row mb-20">
        <div className="col-md-4 mb-20">
          <h1>調達企業一覧</h1>
        </div>
        <div className="col-md-8 mb-20">
          <div className="search-wrapper">
            <form>
              <div className="input-group">
                <div className="input-group-addon">
                  <i className="ion-ios-search" />
                </div>
                <input
                  type="text"
                  className="form-control"
                  name="keyword"
                  maxLength={50}
                  value={keyword}
                  onChange={e => onChangeKeyword(e.target.value)}
                  placeholder="検索"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-4 side-menu mb-40">
          <ConditionList
            name="FUNDINNO利用前調達"
            items={fundingBefores}
            activeItemId={fundingBefore}
            isOpen={isOpenFundingBefores}
            className="funding-before"
            onClick={() => setIsOpenFundingBefores(!isOpenFundingBefores)}
            onClickItem={id => onClickCondition('fundingBefore', id)}
          />
          <ConditionList
            name="FUNDINNO利用後調達"
            items={fundingAfters}
            activeItemId={fundingAfter}
            isOpen={isOpenFundingAfters}
            className="funding-after"
            onClick={() => setIsOpenFundingAfters(!isOpenFundingAfters)}
            onClickItem={id => onClickCondition('fundingAfter', id)}
          />
          <ConditionList
            name="EXIT実績"
            items={exitExperiences}
            activeItemId={exitExperience}
            isOpen={isOpenExitExperiences}
            className="exit-experience"
            onClick={() => setIsOpenExitExperiences(!isOpenExitExperiences)}
            onClickItem={id => onClickCondition('exitExperience', id)}
          />
          <ConditionList
            name="SDGsへの取り組み"
            items={sdgs}
            activeItemId={sdg}
            isOpen={isOpenSdgs}
            className="sdgs"
            onClick={() => setIsOpenSdgs(!isOpenSdgs)}
            onClickItem={id => onClickCondition('sdg', id)}
          />
          <ConditionList
            name="業種"
            items={categories}
            activeItemId={category}
            isOpen={isOpenCategories}
            className="categories"
            onClick={() => setIsOpenCategories(!isOpenCategories)}
            onClickItem={id => onClickCondition('category', id)}
          />
          <ConditionList
            name="ECF複数回調達実績"
            items={ecfFundings}
            activeItemId={ecfFunding}
            isOpen={isOpenEcfFundings}
            className="ecf-funding"
            onClick={() => setIsOpenEcfFundings(!isOpenEcfFundings)}
            onClickItem={id => onClickCondition('ecfFunding', id)}
          />
          <ConditionList
            name="地域"
            items={areas}
            activeItemId={area}
            isOpen={isOpenAreas}
            className="areas"
            onClick={() => setIsOpenAreas(!isOpenAreas)}
            onClickItem={id => onClickCondition('area', id)}
          />
          <ConditionList
            name="調達スキーム"
            items={fundingSchemes}
            activeItemId={fundingScheme}
            isOpen={isOpenFundingSchemes}
            className="funding-schemes"
            onClick={() => setIsOpenFundingSchemes(!isOpenFundingSchemes)}
            onClickItem={id => onClickCondition('fundingScheme', id)}
          />
          <ConditionList
            name="エンジェル税制"
            items={angelTaxes}
            activeItemId={angelTax}
            isOpen={isOpenAngelTaxes}
            className="angel-tax"
            onClick={() => setIsOpenAngelTaxes(!isOpenAngelTaxes)}
            onClickItem={id => onClickCondition('angelTax', id)}
          />
          <ConditionList
            name="ECF利用時のステージ"
            items={ecfStages}
            activeItemId={ecfStage}
            isOpen={isOpenEcfStages}
            className="ecf-stages"
            onClick={() => setIsOpenEcfStages(!isOpenEcfStages)}
            onClickItem={id => onClickCondition('ecfStage', id)}
          />
          <ConditionList
            name="企業の特徴"
            items={features}
            activeItemId={feature}
            isOpen={isOpenFeatures}
            className="features"
            onClick={() => setIsOpenFeatures(!isOpenFeatures)}
            onClickItem={id => onClickCondition('feature', id)}
          />
        </div>
        <div className="col-md-8 company-cards">
          <InfiniteScroll
            pageStart={0}
            loadMore={onLoadMore}
            hasMore={hasNextPage}
            loader={<Loader key={0} type="water" />}
            className="row"
          >
            {companies.map(company => (
              <a
                href={`/companies/${company.id}`}
                key={company.id}
                className="col-md-6"
              >
                <CompanyCard company={company} />
              </a>
            ))}
          </InfiniteScroll>
          {!isLoading && companies.length === 0 && (
            <div className="not-found text-center">
              企業は見つかりませんでした
            </div>
          )}
        </div>
      </div>
    </>
  );
}
